import JSONModel from "sap/ui/model/json/JSONModel";
import BaseController from "./BaseController";

type DatenschutzSection = {
	title: string;
	bodyHtml: string;
};

type DatenschutzDocument = {
	documentTitle: string;
	documentSubtitle: string;
	sections: DatenschutzSection[];
};

const TOP_LEVEL_HEADINGS = new Set([
	"Wer wir sind",
	"Kontaktaufnahme mit dem Datenschutzbeauftragten",
	"Was verstehen wir unter Rechtsgrundlage?",
	"Gemeinsame Nutzung von Daten und internationale Übermittlung",
	"Ihre Rechte",
	"Bereitstellung der Webseite und Erstellung der Logfiles",
	"Verwendung von Cookies",
	"Email-Kontakt",
	"Kontaktformular",
	"Bewerbung per Email",
	"Unternehmensauftritte",
	"Nutzung von Unternehmensauftritten in berufsorientierten Netzwerken",
	"Hosting",
	"Content delivery networks",
	"Integrierte Dienste Dritter",
	"Einsatz von Contact Form 7",
	"Einsatz der Google Marketing Platform",
	"Einsatz von Google AdWords",
]);

const TITLE_PREFIX = /^#\s+/;
const HEADING_PREFIX = /^##\s+/;
const BULLET_PREFIX = /^[-*]\s+/;

/**
 * @namespace ang.order_portal.controller
 */
export default class Datenschutz extends BaseController {
	public onInit(): void {
		const datenschutzModel = new JSONModel({
			sections: [],
		});
		this.getView().setModel(datenschutzModel, "datenschutzModel");
		void this.loadDatenschutzText(datenschutzModel);
	}

	private async loadDatenschutzText(
		datenschutzModel: JSONModel,
	): Promise<void> {
		const response = await fetch("./texts/datenschutz-de.txt");
		if (!response.ok) {
			return;
		}

		const text = await response.text();
		datenschutzModel.setData(this.parseDatenschutzDocument(text));
	}

	private parseDatenschutzDocument(text: string): DatenschutzDocument {
		const lines = text.split(/\r?\n/).map((line) => line.replace(/\s+$/u, ""));
		const bodyStartIndex = lines.findIndex((line) => line.trim().length > 0);
		const bodyLines = bodyStartIndex >= 0 ? lines.slice(bodyStartIndex) : [];

		const sections = this.buildSections(bodyLines);

		return {
			sections,
		};
	}

	private buildSections(lines: string[]): DatenschutzSection[] {
		const sections: DatenschutzSection[] = [];
		let currentSectionTitle = "";
		let currentSectionLines: string[] = [];

		const flushSection = (): void => {
			if (!currentSectionTitle) {
				return;
			}

			sections.push({
				title: currentSectionTitle,
				bodyHtml: this.renderBlocks(currentSectionLines),
			});
		};

		for (const line of lines) {
			const topLevelTitle = this.extractTopLevelTitle(line);
			if (topLevelTitle) {
				flushSection();
				currentSectionTitle = topLevelTitle;
				currentSectionLines = [];
				continue;
			}

			if (currentSectionTitle) {
				currentSectionLines.push(line);
			}
		}

		flushSection();
		return sections;
	}

	private extractTopLevelTitle(line: string): string | null {
		if (TITLE_PREFIX.test(line)) {
			return line.replace(TITLE_PREFIX, "").trim();
		}

		const trimmedLine = line.trim();
		if (TOP_LEVEL_HEADINGS.has(trimmedLine)) {
			return trimmedLine;
		}

		return null;
	}

	private renderBlocks(lines: string[]): string {
		const htmlParts: string[] = [];
		let paragraphLines: string[] = [];
		let bulletItems: string[] = [];
		let addressLines: string[] = [];
		let inExplicitAddressBlock = false;

		const flushParagraph = (): void => {
			if (!paragraphLines.length) {
				return;
			}

			htmlParts.push(`<p>${this.renderInline(paragraphLines.join(" "))}</p>`);
			paragraphLines = [];
		};

		const flushBullets = (): void => {
			if (!bulletItems.length) {
				return;
			}

			htmlParts.push(
				`<ul>${bulletItems
					.map((item) => `<li>${this.renderInline(item)}</li>`)
					.join("")}</ul>`,
			);
			bulletItems = [];
		};

		const flushAddress = (): void => {
			if (!addressLines.length) {
				return;
			}

			htmlParts.push(this.renderAddressBlock(addressLines));
			addressLines = [];
		};

		for (const rawLine of lines) {
			const line = rawLine.trim();

			if (line === "<") {
				flushParagraph();
				flushBullets();
				flushAddress();
				inExplicitAddressBlock = true;
				continue;
			}

			if (line === ">" && inExplicitAddressBlock) {
				flushParagraph();
				flushBullets();
				flushAddress();
				inExplicitAddressBlock = false;
				continue;
			}

			if (inExplicitAddressBlock) {
				if (line) {
					addressLines.push(line);
				}

				continue;
			}

			if (!line) {
				flushParagraph();
				flushBullets();
				flushAddress();
				continue;
			}

			const sectionHeading = this.extractSubHeading(line);
			if (sectionHeading) {
				flushParagraph();
				flushBullets();
				flushAddress();
				htmlParts.push(
					`<h3 class="privacySectionSubheading">${this.renderInline(sectionHeading)}</h3>`,
				);
				continue;
			}

			const bulletItem = BULLET_PREFIX.test(line)
				? line.replace(BULLET_PREFIX, "")
				: null;
			if (bulletItem) {
				flushParagraph();
				flushAddress();
				bulletItems.push(bulletItem.trim());
				continue;
			}

			if (this.isAddressLine(line)) {
				flushParagraph();
				flushBullets();
				addressLines.push(line);
				continue;
			}

			flushBullets();
			flushAddress();
			paragraphLines.push(line);
		}

		flushParagraph();
		flushBullets();
		flushAddress();
		return htmlParts.join("");
	}

	private extractSubHeading(line: string): string | null {
		if (HEADING_PREFIX.test(line)) {
			return line.replace(HEADING_PREFIX, "").trim();
		}

		if (/^\d+\.\s+/.test(line)) {
			return line;
		}

		return null;
	}

	private renderInline(value: string): string {
		const inlinePattern = /\*\*([^*]+)\*\*|\[([^\]]+)\]/g;
		let html = "";
		let lastIndex = 0;
		let match: RegExpExecArray | null;

		while ((match = inlinePattern.exec(value)) !== null) {
			html += this.escapeHtml(value.slice(lastIndex, match.index));

			if (match[1]) {
				html += `<strong>${this.escapeHtml(match[1])}</strong>`;
			} else if (match[2]) {
				const linkTarget = match[2].trim();
				const href = this.normalizeLinkTarget(linkTarget);
				html += `<a href="${this.escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(linkTarget)}</a>`;
			}

			lastIndex = match.index + match[0].length;
		}

		html += this.escapeHtml(value.slice(lastIndex));
		return html;
	}

	private normalizeLinkTarget(value: string): string {
		if (/^https?:\/\//i.test(value)) {
			return value;
		}

		if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
			return `mailto:${value}`;
		}

		if (/^www\./i.test(value)) {
			return `https://${value}`;
		}

		return value;
	}

	private renderAddressBlock(lines: string[]): string {
		return `<address class="privacyAddress">${lines
			.map((line) => this.renderInline(line))
			.join("<br />")}</address>`;
	}

	private isAddressLine(line: string): boolean {
		if (!line || /[.!?]$/.test(line)) {
			return false;
		}

		if (line.length > 80) {
			return false;
		}

		const normalized = line.toLowerCase();
		return (
			/(@|www\.|https?:\/\/|\+\d|\d{4,}|gmbh|gesellschaft|str\.?|straße|hamburg|münchen|deutschland)/i.test(
				normalized,
			) || /^[\d+()\-\s/]+$/.test(line)
		);
	}

	private escapeHtml(value: string): string {
		return value
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#39;");
	}
}
